import type { components } from '../../generated/fct';
import { ApiError, ApiRequest } from '../shared';

type GeneratedCarePlanReportOrgans = components['schemas']['CarePlanReportOrgansSchema'];
type GeneratedCarePlanService = components['schemas']['CarePlanServiceWithoutOrgansSchema'];
type GeneratedOrgan = components['schemas']['OrganSchema'];

export type CareServiceReportOrgansStatus = components['schemas']['StatusEnum'];

export const CARE_SERVICE_ORGANS_PARENT_KEY = {
  ADRENALS: 'adrenals',
  ANKLES: 'ankles',
  BLADDER_AND_URETERS: 'bladder_and_ureters',
  BODY_COMPOSITION: 'body_composition',
  BONY_SKELETON: 'bony_skeleton',
  BOWEL: 'bowel',
  BRAIN: 'brain',
  BREASTS: 'breasts',
  ESOPHAGUS: 'esophagus',
  GALLBLADDER_AND_BILIARY_SYSTEM: 'gallbladder_and_biliary_system',
  HEART_AND_GREAT_VESSELS: 'heart_and_great_vessels',
  KIDNEYS: 'kidneys',
  KNEES: 'knees',
  LIVER: 'liver',
  LUNGS_AND_MEDIASTINUM: 'lungs_and_mediastinum',
  NECK: 'neck',
  OTHER_ORGANS: 'other_organs',
  OVARIES: 'ovaries',
  PANCREAS: 'pancreas',
  PELVIS_AND_HIPS: 'pelvis_and_hips',
  PROSTATE: 'prostate',
  SACROILIAC_JOINTS: 'sacroiliac_joints',
  SHOULDERS: 'shoulders',
  SINUSES_AND_MASTOIDS: 'sinuses_and_mastoids',
  SPINE: 'spine',
  SPLEEN: 'spleen',
  STOMACH: 'stomach',
  UTERUS: 'uterus',
} as const;

export type CareServiceOrgansParentKey =
  (typeof CARE_SERVICE_ORGANS_PARENT_KEY)[keyof typeof CARE_SERVICE_ORGANS_PARENT_KEY];

export const CARE_SERVICE_ORGANS_SUB_KEY = {
  CERVICAL_LYMPH_NODE_CHAIN: 'cervical_lymph_node_chain',
  HYPOPHARYNX: 'hypopharynx',
  NASAL_PHARYNX: 'nasal_pharynx',
  NECK_LYMPH_NODES: 'neck_lymph_nodes',
  ORAL_PHARYNX: 'oral_pharynx',
  THYROID: 'thyroid',
} as const;

export type CareServiceOrgansSubKey =
  (typeof CARE_SERVICE_ORGANS_SUB_KEY)[keyof typeof CARE_SERVICE_ORGANS_SUB_KEY];

export type CareServiceOrgansKey = CareServiceOrgansParentKey | CareServiceOrgansSubKey;

export type CarePlanServiceKey = components['schemas']['CarePlanServiceEnum'];

export const CARE_PLAN_SERVICE_KEY = {
  IMAGING_BASE_ADVANCE_HEART: 'IMAGING.BASE.ADVANCE_HEART',
  IMAGING_BASE_ADVANCE_NEURO: 'IMAGING.BASE.ADVANCE_NEURO',
  IMAGING_BASE_BASIC: 'IMAGING.BASE.BASIC',
  IMAGING_BASE_BODY_COMPOSITION: 'IMAGING.BASE.BODY_COMPOSITION',
  IMAGING_BASE_HEAD_TORSO: 'IMAGING.BASE.HEAD_TORSO',
  IMAGING_BASE_RESEARCH: 'IMAGING.BASE.RESEARCH',
  IMAGING_BASE_STANDARD: 'IMAGING.BASE.STANDARD',
  IMAGING_BASE_TORSO: 'IMAGING.BASE.TORSO',
  IMAGING_LEGACY_DEFAULT: 'IMAGING.LEGACY.DEFAULT',
  IMAGING_LEGACY_SKU_100: 'IMAGING.LEGACY.SKU_100',
  IMAGING_LEGACY_SKU_200: 'IMAGING.LEGACY.SKU_200',
  IMAGING_LEGACY_SKU_300: 'IMAGING.LEGACY.SKU_300',
  IMAGING_LEGACY_SKU_350: 'IMAGING.LEGACY.SKU_350',
  IMAGING_LEGACY_SKU_370: 'IMAGING.LEGACY.SKU_370',
} as const satisfies Record<string, CarePlanServiceKey>;

export const CARE_PLAN_SERVICE_NAME = {
  IMAGING_BASE_ADVANCE_HEART: 'Heart Health Assessment', // TODO: Update this to the correct name once CARE_PLAN_SERVICE_NAME have it
  IMAGING_BASE_ADVANCE_NEURO: 'Brain Health Assessment', // TODO: Update this to the correct name once CARE_PLAN_SERVICE_NAME have it
  IMAGING_BASE_BASIC: 'Focused Scan',
  IMAGING_BASE_BODY_COMPOSITION: 'Body Composition Analysis',
  IMAGING_BASE_HEAD_TORSO: 'Head and Torso Scan',
  IMAGING_BASE_RESEARCH: 'Research',
  IMAGING_BASE_STANDARD: 'Whole Body Scan',
  IMAGING_BASE_TORSO: 'Torso Scan',
  IMAGING_LEGACY_DEFAULT: 'Default Scan',
  IMAGING_LEGACY_SKU_100: 'Torso Scan',
  IMAGING_LEGACY_SKU_200: 'Head and Torso Scan',
  IMAGING_LEGACY_SKU_300: 'Whole Body Scan',
  IMAGING_LEGACY_SKU_350: 'Whole Body Scan with NeuroQuant and HepaFat',
  IMAGING_LEGACY_SKU_370: 'Enhanced Screening with Whole Body Scan',
  IMAGING_LEGACY_SKU_400: 'Whole Body Scan with Priority Access',
  IMAGING_LEGACY_SKU_500: 'Research and Development',
} as const;

export type CarePlanServiceName =
  (typeof CARE_PLAN_SERVICE_NAME)[keyof typeof CARE_PLAN_SERVICE_NAME];

export type CarePlanService = GeneratedCarePlanService;

export type CareServiceReportOrgan = Omit<GeneratedOrgan, 'key' | 'subOrgans'> &
  Required<Pick<GeneratedOrgan, 'createdAt' | 'uuid'>> & {
    key: CareServiceOrgansKey | string;
    subOrgans: CareServiceReportOrgan[];
  };

export type CareServiceReportOrgansStructure = Omit<
  GeneratedCarePlanReportOrgans,
  'carePlanServices' | 'disabledOrgans' | 'enabledOrgans'
> &
  Required<Pick<GeneratedCarePlanReportOrgans, 'uuid'>> & {
    carePlanServices: CarePlanService[];
    createdAt: string;
    createdBy: string;
    disabledOrgans: CareServiceReportOrgan[];
    enabledOrgans: CareServiceReportOrgan[];
  };

export interface CareServiceReportOrgansPayload {
  studyId: string;
}

export type CareServiceReportOrgansRequest = ApiRequest<Partial<CareServiceReportOrgansPayload>>;

export type CareServiceReportOrgansResponse = Partial<CareServiceReportOrgansStructure>;

export type CareServiceReportOrgansError = ApiError<unknown>;

export type PostCareServiceReportOrgansPayload = Pick<
  components['schemas']['CompositionCreateRequest'],
  'carePlanServices' | 'studyUuid'
>;

export type PostCareServiceReportOrgansRequest = ApiRequest<PostCareServiceReportOrgansPayload>;

export type PostCareServiceReportOrgansResponse = CareServiceReportOrgansStructure;

export type PostCareServiceReportOrgansError = ApiError<unknown>;
